import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';
import { Account } from 'model/IChat';
import { unstable_batchedUpdates } from 'react-dom';
import { WlWindow } from 'typings/globals/cordova';
import errorMessages from 'modules/authentication/helpers/authErrors';
import { isAndroidApp } from 'utils/platform';

declare const window: WlWindow;

export const getCsrfToken = async (): Promise<string> => {
  try {
    const csrfResponse = await fetch(`${URL_STOREFRONT_HOST}/api/auth/csrf`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      credentials: 'include'
    });
    const csrf = await csrfResponse.json();
    return csrf.csrfToken;
  } catch (exception) {
    console.error('Error getting CSRF token', exception);
  }
  return null;
};

export const signIn = async (provider: string, callbackUrl = '/', onFinish: ({ error, success, redirectUrl }: {
  error?: any;
  success: boolean;
  redirectUrl?: string;
}) => void): Promise<void> => {
  try {
    const csrfToken = await getCsrfToken();

    const params = new URLSearchParams();
    params.set('callbackUrl', callbackUrl ?? '/');
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');

    const signinResponse = await fetch(`${URL_STOREFRONT_HOST}/api/auth/signin/${provider}`, {
      method: 'POST',
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      credentials: 'include',
      body: params.toString()
    });

    const signin = await signinResponse.json();

    const { url } = signin;
    if (isCordova()) {
      const browser = window.cordova.InAppBrowser.open(url, '_blank', 'location=no');
      browser.addEventListener('loadstart', (event: any) => {
        if (event.url.startsWith('https://test.withlocals.com') || event.url.startsWith('https://www.withlocals.com') || event.url.startsWith('https://withlocals.com')) {
          if (event.url.includes('api/auth/callback')) {
            browser.hide();
            return;
          } else {
            browser.close();
            onFinish({ success: true, redirectUrl: callbackUrl });
            return;
          }
        }
      });
    } else {
      onFinish({ success: true, redirectUrl: url });
    }
  } catch (error) {
    onFinish({ success: false, error });
    console.error('[signIn] Error signing in', error, provider, callbackUrl);
  }
};

export const signInWithCredentials = async (username: string, password: string, callbackUrl: string): Promise<void> => {
  try {
    const csrfToken = await getCsrfToken();

    if (!csrfToken) {
      throw new Error('Failed to get CSRF token');
    }

    const params = new URLSearchParams();
    params.set('username', username);
    params.set('password', password);
    params.set('callbackUrl', callbackUrl);
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');
    params.set('redirect', 'false');

    const signinResponse = await fetch(`${URL_STOREFRONT_HOST}/api/auth/callback/credentials`, {
      method: 'POST',
      headers: {
        'Accept': '*/*',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      credentials: 'include',
      body: params.toString()
    });

    const signin = await signinResponse.json();

    if (!signinResponse.ok) {
      const url = new URL(signin.url);

      const errorSearchParams = new URLSearchParams(url.search);
      throw new Error(errorMessages.get(errorSearchParams.get('error')));
    }
    console.log('signinResponse', signinResponse.ok);

    await fetchAccountInformation(useAuthStore.getState());

    if (isCordova()) {
      window.location.href = callbackUrl;
    } else {
      console.log('signin response', signin);
      const { url } = signin;

      window.location.href = url;
    }
  } catch (exception) {
    console.error('[signInWithCredentials] Error signing in', exception, callbackUrl);
    throw exception;
  }
};

export const signUpWithCredentials = async ({
  firstName,
  lastName,
  email,
  password,
  gender,
  phoneNumber,
  callbackUrl
}: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  gender?: string;
  phoneNumber?: string;
  callbackUrl?: string;
}): Promise<void> => {
  const body = {
    firstName,
    email,
    password,
    lastName,
    gender,
    phoneNumber
  };

  // Creating new account
  const signupResponse = await fetch(`${URL_STOREFRONT_HOST}/storefront-api/v1/auth/signup/`, {
    method: 'POST',
    headers: {
      'Accept': '*/*',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
  if(!signupResponse.ok) {
    const signup = await signupResponse.json();
    throw new Error(signup.error);
  }

  // Sign in with the account that has just been created
  await signInWithCredentials(email, password, callbackUrl);
};

export const signOut = async (callbackUrl: string): Promise<void> => {
  try {
    const csrfToken = await getCsrfToken();

    const params = new URLSearchParams();
    params.set('callbackUrl', callbackUrl);
    params.set('csrfToken', csrfToken);
    params.set('json', 'true');

    const signoutResponse = await fetch(`${URL_STOREFRONT_HOST}/api/auth/signout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      credentials: 'include',
      body: params.toString()
    });

    void await signoutResponse.json();
    unstable_batchedUpdates(() => {
      useAuthStore.getState().logout();
    });
    window.location.href = '/';
  } catch (exception) {
    console.error('Error signing out', exception);
  }

};

/**
 *
 * @param state
 * @param options.silent - if true, will not set loading state
 */
export const fetchAccountInformation = async (state: any, options: {
  silent: boolean;
} = { silent: false }): Promise<void> => {
  if (!options.silent) {
    useAuthStore.setState({ loading: true });
  }
  const tokenFromStorage = state.token;

  try {
    const requestOptions: { [key: string]: any } = {
      method: 'POST',
      credentials: 'include'
    };

    if (tokenFromStorage) {
      requestOptions.headers = { Authorization: `Bearer ${tokenFromStorage}` };
    }

    const token = await fetch(
      `${URL_STOREFRONT_HOST}/storefront-api/v1/auth/token`,
      requestOptions
    );

    const tokenResponse = await token.json();

    if (tokenResponse.token) {
      useAuthStore.getState().setUser(tokenResponse.user);
      useAuthStore.getState().setSpy(tokenResponse?.spy);
      useAuthStore.getState().setToken(tokenResponse.token);
    }
  } catch (exception) {
    console.warn('Error verifying token', exception);
    const shouldCheckForTooLargeCookies = isAndroidApp() && window.cookieMaster;

    if (shouldCheckForTooLargeCookies) {
      forceTokenRemoval(exception, true);
      return;
    }
  }

  try {
    const requestOptions: { [key: string]: any } = {
      method: 'GET',
      credentials: 'include'
    };

    if (tokenFromStorage) {
      requestOptions.headers = { Authorization: `Bearer ${tokenFromStorage}` };
    }

    const accountResponse = await fetch(`${URL_API_HOST}/api/v1/account`, requestOptions);

    if (accountResponse.status !== 200) {
      throw new Error('account');
    }

    const accountJson: Account = await accountResponse.json();

    useAuthStore.setState({ account: accountJson });

    const realAccountResponse = await fetch(`${URL_API_HOST}/api/v1/account/me`, requestOptions);


    if (realAccountResponse.status !== 200) {
      throw new Error('real account');
    }

    const realAccountJson = await realAccountResponse.json();

    useAuthStore.setState({ realAccount: realAccountJson });
  } catch (exception) {
    console.warn(`Error fetching ${exception.message}`);
  }

  useAuthStore.setState({ loading: false });
};

export const forceTokenRemoval = (error: any, shouldReload: boolean) => {
  useAuthStore.getState().logout();
  window.cookieMaster.clearCookies(
    () => {
      console.log('Cookies have been cleared because of possible 431 response code', error);
      if (shouldReload) {
        window.location.reload();
      }
    },
    () => {
      console.error('Cookies could not be cleared');
      if (shouldReload) {
        window.location.reload();
      }
    }
  );
};